/***
 *
 *   SCREENING CHECK TYPES
 *   The manual-screening check types offered on the Screening page. Each type
 *   owns its form layout: which fields it asks for, their labels, and the
 *   checks it posts to POST /api/direct-verification/screen. Adding a check
 *   type (or changing what one asks for) is a change to this list only.
 *
 *   Pure: no React, no network, so it is pinned by screening-types.test.js.
 *
 **********/

// Middle names go with the first name: Dilisense is searched on
// "<first> <last>" and iDenfy on { name, surname }, so there is no
// separate middle-name field to send.
const NAME_FIELDS = (seed) => ({
  firstName: {
    label: "First and middle name(s)",
    type: "text",
    required: true,
    placeholder: "e.g. John Paul",
    errorMessage: "Please enter first name.",
    value: seed.firstName || "",
  },
  lastName: {
    label: "Last name",
    type: "text",
    required: true,
    placeholder: "e.g. Smith",
    errorMessage: "Please enter last name.",
    value: seed.lastName || "",
  },
});

// DateInput (a native date input) takes `max` as "YYYY-MM-DD" or
// { year, month, day }; local today, so the picker never offers a later day.
const DOB_FIELD = (seed, today, label) => ({
  dob: {
    label,
    type: "date",
    value: seed.dob || "",
    max: {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    },
  },
});

export const SCREENING_TYPES = [
  {
    id: "sanctions",
    label: "PEP/Sanctions",
    checks: ["sanctions"],
    description:
      "Screens the name against PEP and sanctions lists. A date of birth narrows the matches: listed people with a different date of birth are left out, people with none on record are kept.",
    hasDob: true,
    layout: (seed, today) => ({
      ...NAME_FIELDS(seed),
      ...DOB_FIELD(seed, today, "Date of birth (optional)"),
    }),
  },
  {
    id: "adverse_media",
    label: "Adverse Media",
    checks: ["adverse_media"],
    description: "Searches news coverage for the name.",
    hasDob: false,
    layout: (seed) => ({
      ...NAME_FIELDS(seed),
    }),
  },
  {
    id: "sanctions+adverse_media",
    label: "PEP/Sanctions + Adverse Media",
    checks: ["sanctions", "adverse_media"],
    description:
      "Runs both checks in one go. The date of birth narrows the PEP/Sanctions matches only.",
    hasDob: true,
    layout: (seed, today) => ({
      ...NAME_FIELDS(seed),
      ...DOB_FIELD(seed, today, "Date of birth (optional, used for PEP/Sanctions)"),
    }),
  },
];

/**
 * The check types an account may run, given GET /api/direct-verification/me's
 * `manual_screening` projection: a type is offered only when every check it
 * runs is entitled.
 */
export function offeredScreeningTypes(manualScreening) {
  return SCREENING_TYPES.filter((type) =>
    type.checks.every((check) => manualScreening?.[check] === true)
  );
}

/**
 * The Form data for one check type, seeded with values carried over from the
 * previous layout (typed names survive a type switch). `checks` rides along as
 * a hidden input, so the form posts check identifiers, never labels.
 */
export function buildScreeningForm(type, seed = {}, today = new Date()) {
  return {
    ...type.layout(seed, today),
    checks: {
      type: "hidden",
      value: [...type.checks],
    },
  };
}
